var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { Button, Typography, Space, Spin, Alert, Input, Steps, message } from 'antd';
import { CheckCircleOutlined, LoadingOutlined, QrcodeOutlined, SafetyOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Auth } from '@whatsper/texterchat-common';
import QRCode from 'qrcode';
import ModalWithoutBottom from '../Chat/common/ModalWithoutBottom/ModalWithoutBottom';
import useCheckIfMobile from '../../helpers/hooks/useCheckIfMobile';
import { useStore } from '../../store/rootStore';
import s from './TOTPSetup.module.scss';
const { Title, Text, Paragraph } = Typography;
const TOTPSetup = observer(() => {
    var _a;
    const { t } = useTranslation();
    const history = useHistory();
    const isMobile = useCheckIfMobile();
    const { loginStore } = useStore();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totpCredentials, setTotpCredentials] = useState(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [password, setPassword] = useState('');
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
    const [secretKey, setSecretKey] = useState(null);
    // Check if user has password auth (to determine if password is required)
    const hasPasswordAuth = (_a = loginStore.availableAuthMethods) === null || _a === void 0 ? void 0 : _a.includes(Auth.Methods.Method.password);
    const onBack = () => {
        history.push('/');
    };
    // Generate QR code from URI
    const generateQRCode = (uri) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const dataUrl = yield QRCode.toDataURL(uri, {
                width: 256,
                margin: 2,
                color: {
                    dark: '#383C3A',
                    light: '#ffffff',
                },
            });
            setQrCodeDataUrl(dataUrl);
            const url = new URL(uri);
            const secret = url.searchParams.get('secret');
            if (secret) {
                setSecretKey(secret);
            }
        }
        catch (err) {
            console.error('Failed to generate QR code', err);
            setError(t('totpQrCodeError'));
        }
    });
    // Step 1: Get temporary credentials
    const fetchCredentials = () => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        setError(null);
        try {
            const credentials = yield loginStore.getTOTPTemporaryCredentials();
            if (credentials) {
                setTotpCredentials(credentials);
                yield generateQRCode(credentials.uri);
                setCurrentStep(1);
            }
        }
        catch (err) {
            setError(err.message || t('totpCredentialsError'));
        }
        finally {
            setLoading(false);
        }
    });
    // Step 2: Verify and enable TOTP
    const enableTOTP = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!totpCredentials)
            return;
        if (!verificationCode || verificationCode.length !== 6) {
            setError(t('totpInvalidCode'));
            return;
        }
        setLoading(true);
        setError(null);
        try {
            yield loginStore.enableTOTP(totpCredentials.id, verificationCode, hasPasswordAuth ? password : undefined);
            message.success(t('totpEnabledSuccess'));
            setCurrentStep(2);
        }
        catch (err) {
            setError(err.message || t('totpEnableError'));
        }
        finally {
            setLoading(false);
        }
    });
    // Start the setup process on mount
    useEffect(() => {
        fetchCredentials();
    }, []);
    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (React.createElement("div", { className: s.stepContent }, loading ? (React.createElement("div", { className: s.loadingContainer },
                    React.createElement(Spin, { size: "large", tip: t('totpLoadingCredentials') }))) : error ? (React.createElement(Alert, { type: "error", message: error, action: React.createElement(Button, { size: "small", onClick: fetchCredentials },
                        React.createElement(Trans, { i18nKey: "retry" })) })) : null));
            case 1:
                return (React.createElement("div", { className: s.stepContent },
                    React.createElement("div", { className: s.qrSection },
                        React.createElement(Title, { level: 5 },
                            React.createElement(Trans, { i18nKey: "totpScanQrTitle" })),
                        React.createElement(Paragraph, { type: "secondary" },
                            React.createElement(Trans, { i18nKey: "totpScanQrDescription" })),
                        qrCodeDataUrl ? (React.createElement("div", { className: s.qrCodeContainer },
                            React.createElement("img", { src: qrCodeDataUrl, alt: "TOTP QR Code", className: s.qrCode }))) : (React.createElement("div", { className: s.qrCodePlaceholder },
                            React.createElement(Spin, { size: "large" }))),
                        secretKey && (React.createElement("div", { className: s.manualEntry },
                            React.createElement(Text, { type: "secondary" },
                                React.createElement(Trans, { i18nKey: "totpManualEntryLabel" })),
                            React.createElement(Input.Password, { value: secretKey, readOnly: true, className: s.secretKeyInput, onClick: (e) => {
                                    e.target.select();
                                    navigator.clipboard.writeText(secretKey);
                                    message.success(t('copiedToClipboard'));
                                } })))),
                    React.createElement("div", { className: s.verifySection },
                        React.createElement(Title, { level: 5 },
                            React.createElement(Trans, { i18nKey: "totpVerifyTitle" })),
                        React.createElement(Paragraph, { type: "secondary" },
                            React.createElement(Trans, { i18nKey: "totpVerifyDescription" })),
                        React.createElement(Space, { direction: "vertical", size: "middle", className: s.verifyForm },
                            React.createElement("div", { className: s.inputGroup },
                                React.createElement("label", null,
                                    React.createElement(Trans, { i18nKey: "totpCode" })),
                                React.createElement(Input, { placeholder: "123456", value: verificationCode, onChange: (e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6)), maxLength: 6, inputMode: "numeric", autoComplete: "one-time-code", className: s.codeInput, size: "large" })),
                            hasPasswordAuth && (React.createElement("div", { className: s.inputGroup },
                                React.createElement("label", null,
                                    React.createElement(Trans, { i18nKey: "currentPassword" })),
                                React.createElement(Input.Password, { placeholder: t('enterCurrentPassword'), value: password, onChange: (e) => setPassword(e.target.value), className: s.passwordInput, size: "large" }))),
                            error && React.createElement(Alert, { type: "error", message: error, showIcon: true }),
                            React.createElement(Button, { type: "primary", size: "large", onClick: enableTOTP, loading: loading, disabled: verificationCode.length !== 6 || (hasPasswordAuth && !password), className: s.enableButton },
                                React.createElement(Trans, { i18nKey: "totpEnableButton" }))))));
            case 2:
                return (React.createElement("div", { className: s.stepContent },
                    React.createElement("div", { className: s.successContainer },
                        React.createElement(CheckCircleOutlined, { className: s.successIcon }),
                        React.createElement(Title, { level: 4 },
                            React.createElement(Trans, { i18nKey: "totpSetupComplete" })),
                        React.createElement(Paragraph, null,
                            React.createElement(Trans, { i18nKey: "totpSetupCompleteDescription" })),
                        React.createElement(Button, { type: "primary", size: "large", onClick: onBack },
                            React.createElement(Trans, { i18nKey: "done" })))));
            default:
                return null;
        }
    };
    const steps = [
        {
            title: t('totpStepPrepare'),
            icon: currentStep === 0 && loading ? React.createElement(LoadingOutlined, null) : React.createElement(SafetyOutlined, null),
        },
        {
            title: t('totpStepScan'),
            icon: React.createElement(QrcodeOutlined, null),
        },
        {
            title: t('totpStepComplete'),
            icon: React.createElement(CheckCircleOutlined, null),
        },
    ];
    return (React.createElement(ModalWithoutBottom, { setModalVisible: onBack, titleTextKey: "totpSetupTitle", width: isMobile ? undefined : 600, visible: true },
        React.createElement("div", { className: s.root },
            React.createElement(Steps, { current: currentStep, items: steps, className: s.steps, size: isMobile ? 'small' : 'default' }),
            renderStepContent())));
});
export default TOTPSetup;
